import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { pick } from "@/i18n/translations";
import { OfferCard, WhatsappCTA, NewsletterSection } from "@/pages/Home";
import { Filter } from "lucide-react";

export default function Offers() {
    const { t, lang } = useLang();
    const [offers, setOffers] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState("");

    useEffect(() => {
        api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
        api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    }, []);

    const filtered = selected ? offers.filter((o) => o.category_slug === selected) : offers;
    const featured = filtered.filter((o) => o.featured);
    const rest = filtered.filter((o) => !o.featured);

    return (
        <PageShell>
            {/* Hero */}
            <section className="relative bg-brand-green text-white overflow-hidden">
                <div className="absolute inset-0 grainy opacity-50" />
                <div className="container-x relative py-20 md:py-24">
                    <div className="eyebrow text-brand-yellow">{t.offers.eyebrow}</div>
                    <h1 className="mt-4 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl text-balance max-w-3xl">{t.offers.title}</h1>
                    <p className="mt-5 text-white/80 max-w-2xl text-lg">{t.offers.heroDesc}</p>
                </div>
            </section>

            {/* Filters */}
            <section className="container-x py-10">
                <div className="flex items-center gap-3 flex-wrap" data-testid="offers-filters">
                    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-charcoal/50">
                        <Filter className="h-3.5 w-3.5" /> {t.offers.allCategories}
                    </span>
                    <button
                        onClick={() => setSelected("")}
                        data-testid="filter-all"
                        className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                            selected === "" ? "bg-brand-charcoal text-white" : "bg-white border border-black/10 text-brand-charcoal hover:border-brand-charcoal"
                        }`}
                    >
                        {t.offers.allCategories}
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            data-testid={`filter-${c.slug}`}
                            onClick={() => setSelected(c.slug)}
                            className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
                                selected === c.slug ? "bg-brand-charcoal text-white" : "bg-white border border-black/10 text-brand-charcoal hover:border-brand-charcoal"
                            }`}
                        >
                            {pick(c.name, lang)}
                        </button>
                    ))}
                </div>

                {featured.length > 0 && (
                    <div className="mt-10">
                        <div className="eyebrow">{t.offers.featured}</div>
                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {featured.map((o) => <OfferCard key={o.id} offer={o} lang={lang} t={t} />)}
                        </div>
                    </div>
                )}

                <div className="mt-12">
                    <div className="eyebrow">{t.offers.all}</div>
                    {rest.length === 0 ? (
                        <div className="mt-5 rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center text-sm text-brand-charcoal/60">
                            {t.offers.empty}
                        </div>
                    ) : (
                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {rest.map((o) => <OfferCard key={o.id} offer={o} lang={lang} t={t} />)}
                        </div>
                    )}
                </div>
            </section>

            <WhatsappCTA />
            <NewsletterSection />
        </PageShell>
    );
}
